import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import ApiInspector from '../../components/ApiInspector';
import { apiDownload, apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { useFiltriApplicati } from '../../hooks/useFiltriApplicati';
import BopLoaderOverlay from '../../components/BopLoaderOverlay';
import { formatNumber } from '../../utils/formatters';
import { BustaGo, Importo, StatoGo, dataIt, euro, num } from '../../lib/gestionaleOpen';
import { API_DOCS } from './ContoEconomicoGoPage.api';
import { BottoneCercaGo, InvitoCercaGo } from './CercaGo';

/**
 * Conto economico di una societa' dalla contabilita' generale di Gestionale
 * Open, SEMPRE per competenza di bilancio.
 *
 * - Fonte: solo i conti economici della prima nota, senza aperture e chiusure.
 *   Le commesse non influiscono: nessun dato viene dai movimenti di commessa.
 * - Verificato il 23/09/2026: per ogni esercizio chiuso il risultato coincide
 *   con l'utile rilevato dall'amministrazione, su tutte le societa'.
 * - Le voci sono la classificazione del piano dei conti della societa' (fino a
 *   tre livelli, poi il conto): per questo una societa' alla volta.
 *
 * Due schede: lo stesso intervallo di mesi su tre anni affiancati, oppure un
 * anno mese per mese. Clic su un conto: le sue registrazioni.
 */

type Modo = 'anni' | 'mesi';

type Criterio = 'competenze' | 'contabile';

interface Colonna { chiave: string; etichetta: string; da: string; a: string; criterio?: Criterio }

interface NodoCE {
  chiave: string;
  livello: number;
  codice: string | null;
  descrizione: string | null;
  valori: Record<string, number>;
  totale: number;
  figli: NodoCE[];
}

interface RispostaCE {
  database: string;
  modo: Modo;
  periodo: { anno: number; mese_da: number; mese_a: number; descrizione: string };
  colonne: Colonna[];
  criterio?: string;
  voci: NodoCE[];
  ricavi: Record<string, number>;
  costi: Record<string, number>;
  totali: Record<string, number>;
  periodo_aperto: boolean;
}

interface Registrazione {
  id_riga: number;
  progressivo: number;
  data_competenza: string | null;
  data_registrazione: string | null;
  causale: string | null;
  descrizione_causale: string | null;
  descrizione: string | null;
  documento_tipo: string | null;
  numero_documento: string | null;
  controparte_nome: string | null;
  dare: Importo;
  avere: Importo;
  importo: Importo;
  // Solo con competenze: 'quota' = parte di un periodo di competenza
  tipo_competenza?: 'data' | 'quota';
  riga?: number;
  competenza_dal?: string | null;
  competenza_al?: string | null;
  // Sulle quote: importo di tutto il periodo e giorni che cadono nel periodo scelto
  importo_intero?: Importo;
  giorni_nel_periodo?: number | null;
  giorni_totali?: number | null;
}

interface RispostaRegistrazioni {
  totale: number;
  importo?: Importo;
  has_more: boolean;
  data: Registrazione[];
}

const ANNO_CORRENTE = new Date().getFullYear();
const MESE_CORRENTE = new Date().getMonth() + 1;
// Default: l'ultimo mese chiuso, come le stampe di GO a fine mese (a gennaio
// e' dicembre dell'anno prima)
const ANNO_ULTIMO_CHIUSO = MESE_CORRENTE === 1 ? ANNO_CORRENTE - 1 : ANNO_CORRENTE;
const MESE_ULTIMO_CHIUSO = MESE_CORRENTE === 1 ? 12 : MESE_CORRENTE - 1;
const ANNI = Array.from({ length: 6 }, (_, i) => ANNO_CORRENTE - i);
const NOMI_MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const PAGINA_REGISTRAZIONI = 500;

const LIVELLI = [
  { valore: 1, etichetta: 'Voci principali' },
  { valore: 2, etichetta: 'Fino al 2° livello' },
  { valore: 3, etichetta: 'Fino al 3° livello' },
  { valore: 4, etichetta: 'Fino ai conti' },
];

function Valore({ valore }: { valore: number }) {
  return <span className={valore < 0 ? 'bop-text-danger' : undefined}>{euro(valore)}</span>;
}

function variazione(prima: number, dopo: number): { assoluta: number; perc: number | null } {
  const assoluta = Math.round((dopo - prima) * 100) / 100;
  return { assoluta, perc: prima ? Math.round((assoluta / Math.abs(prima)) * 1000) / 10 : null };
}

/** Tutte le chiavi dei nodi fino al livello indicato (per "espandi fino a"). */
function chiaviFinoA(voci: NodoCE[], livello: number): Set<string> {
  const aperti = new Set<string>();
  const visita = (n: NodoCE) => {
    if (n.livello < livello && n.figli.length) {
      aperti.add(n.chiave);
      n.figli.forEach(visita);
    }
  };
  voci.forEach(visita);
  return aperti;
}

export default function ContoEconomicoGoPage() {
  const { societaId: societaSel, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  // Filtri: i controlli cambiano la bozza (…Sel), la lettura usa i valori
  // applicati con «Cerca». Societa' e scheda (anni/mesi) si applicano subito,
  // ma rileggono solo dopo la prima ricerca: la pagina parte vuota.
  const [modoSel, setModo] = useState<Modo>('anni');
  const [annoSel, setAnno] = useState<number>(ANNO_ULTIMO_CHIUSO);
  const [meseDaSel, setMeseDa] = useState<number>(1);
  const [meseASel, setMeseA] = useState<number>(MESE_ULTIMO_CHIUSO);
  const { applicati, versione, applica, modificati, cercato } = useFiltriApplicati(
    { societaId: societaSel, modo: modoSel, anno: annoSel, meseDa: meseDaSel, meseA: meseASel },
    `${societaSel}|${modoSel}`,
  );
  const { societaId, modo, anno, meseDa, meseA } = applicati;
  // Il livello di dettaglio apre e chiude l'albero gia' letto: resta immediato
  const [livello, setLivello] = useState<number>(2);

  const [dati, setDati] = useState<RispostaCE | null>(null);
  const [stato, setStato] = useState<StatoGo>('OK');
  const [avviso, setAvviso] = useState('');
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);
  const [aperti, setAperti] = useState<Set<string>>(new Set());
  const [esportando, setEsportando] = useState<'excel' | 'pdf' | null>(null);

  const [contoAperto, setContoAperto] = useState<NodoCE | null>(null);

  // Cambiando anno, sull'anno in corso si arriva all'ultimo mese chiuso; sugli altri a dicembre
  const cambiaAnno = (a: number) => {
    setAnno(a);
    setMeseA(a === ANNO_CORRENTE ? Math.max(MESE_ULTIMO_CHIUSO, meseDaSel) : 12);
  };

  const parametri = useCallback(() => new URLSearchParams({
    societaId, modo, anno: String(anno), mese_da: String(meseDa), mese_a: String(meseA),
  }), [societaId, modo, anno, meseDa, meseA]);

  useEffect(() => {
    if (!cercato || !societaId) { setDati(null); setStato('OK'); return undefined; }
    let annullato = false;
    (async () => {
      setCaricando(true);
      setErrore('');
      setAvviso('');
      try {
        // @api GET /api/bop-go/conto-economico?societaId=1&modo=anni&anno=2026&mese_da=1&mese_a=8 -> {"success":true,"stato":"OK","data":{"modo":"anni","periodo":{"descrizione":"gen–ago 2024–2026"},"colonne":[{"chiave":"2024","etichetta":"2024","da":"2024-01-01","a":"2024-08-31"}],"voci":[{"chiave":"RI","livello":1,"codice":"RI","descrizione":"Ricavi","valori":{"2024":2377045},"totale":2377045,"figli":[]}],"ricavi":{"2024":2377045},"costi":{"2024":-2311520},"totali":{"2024":65525},"periodo_aperto":true}}
        const res = await apiFetch<BustaGo<RispostaCE>>(`/api/bop-go/conto-economico?${parametri().toString()}`);
        if (annullato) return;
        setStato(res.stato);
        setDati(res.data);
        if (res.stato !== 'OK' && res.error) setAvviso(res.error);
      } catch (err) {
        if (annullato) return;
        setErrore(err instanceof Error ? err.message : 'Errore nel recupero del conto economico da GO');
        setDati(null);
      } finally {
        if (!annullato) setCaricando(false);
      }
    })();
    return () => { annullato = true; };
  }, [cercato, societaId, parametri, versione]);

  // Il livello di dettaglio apre l'albero fino a quel livello
  useEffect(() => {
    setAperti(dati ? chiaviFinoA(dati.voci, livello) : new Set());
  }, [dati, livello]);

  const alterna = (chiave: string) => {
    setAperti((prec) => {
      const nuovo = new Set(prec);
      if (nuovo.has(chiave)) nuovo.delete(chiave); else nuovo.add(chiave);
      return nuovo;
    });
  };

  const esporta = async (tipo: 'excel' | 'pdf') => {
    if (!dati) return;
    setEsportando(tipo);
    setErrore('');
    try {
      const nome = `conto-economico_${dati.periodo.descrizione}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (tipo === 'excel') {
        // @api GET /api/bop-go/conto-economico/export/excel?societaId=1&modo=anni&anno=2026&mese_da=1&mese_a=8&livello=2 -> file xlsx
        await apiDownload(`/api/bop-go/conto-economico/export/excel?${parametri().toString()}&livello=${livello}`, `${nome}.xlsx`);
      } else {
        // @api GET /api/bop-go/conto-economico/export/pdf?societaId=1&modo=anni&anno=2026&mese_da=1&mese_a=8&livello=2 -> file pdf
        await apiDownload(`/api/bop-go/conto-economico/export/pdf?${parametri().toString()}&livello=${livello}`, `${nome}.pdf`);
      }
    } catch (err) {
      setErrore(`Export non riuscito: ${err instanceof Error ? err.message : 'errore sconosciuto'}`);
    } finally {
      setEsportando(null);
    }
  };

  const colonne = dati?.colonne ?? [];
  const conVariazione = dati?.modo === 'anni' && colonne.length >= 2;
  const conTotale = dati?.modo === 'mesi';
  const [penultima, ultima] = colonne.slice(-2).map((c) => c.chiave);

  // Righe visibili dell'albero, nell'ordine di lettura
  const righeVisibili = useMemo(() => {
    const out: NodoCE[] = [];
    const visita = (n: NodoCE) => {
      out.push(n);
      if (aperti.has(n.chiave)) n.figli.forEach(visita);
    };
    (dati?.voci ?? []).forEach(visita);
    return out;
  }, [dati, aperti]);

  const cellaValori = (valori: Record<string, number>) => {
    const tot = colonne.reduce((s, c) => s + (valori[c.chiave] || 0), 0);
    const v = conVariazione ? variazione(valori[penultima] || 0, valori[ultima] || 0) : null;
    return (
      <>
        {colonne.map((c) => (
          <td key={c.chiave} className="bop-conto-economico__valore"><Valore valore={valori[c.chiave] || 0} /></td>
        ))}
        {conTotale && <td className="bop-conto-economico__valore bop-conto-economico__valore--totale"><Valore valore={Math.round(tot * 100) / 100} /></td>}
        {v && (
          <>
            <td className="bop-conto-economico__valore"><Valore valore={v.assoluta} /></td>
            <td className="bop-conto-economico__valore bop-text-muted">{v.perc === null ? '—' : `${formatNumber(v.perc)}%`}</td>
          </>
        )}
      </>
    );
  };

  const ultimaColonna = colonne[colonne.length - 1];

  return (
    <Layout title="Conto economico">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Conto economico</h1>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" disabled={!dati || stato !== 'OK' || !!esportando} onClick={() => void esporta('excel')}>
            {esportando === 'excel' ? 'Esportazione…' : 'Excel'}
          </button>
          <button className="bop-btn bop-btn-secondary" disabled={!dati || stato !== 'OK' || !!esportando} onClick={() => void esporta('pdf')}>
            {esportando === 'pdf' ? 'Generazione PDF…' : 'PDF'}
          </button>
        </div>
      </div>

      <p className="bop-text-muted">
        Il conto economico dalla contabilità generale di Gestionale Open, sempre per
        <strong> competenza di bilancio</strong>: solo i conti economici, senza le scritture di
        apertura e chiusura. Le commesse non influiscono. Per ogni esercizio chiuso il risultato
        coincide con l'utile rilevato in contabilità.
      </p>

      <div className="bop-tabs">
        <button className={`bop-tab ${modoSel === 'anni' ? 'active' : ''}`} onClick={() => setModo('anni')}>Anni a confronto</button>
        <button className={`bop-tab ${modoSel === 'mesi' ? 'active' : ''}`} onClick={() => setModo('mesi')}>Mese per mese</button>
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaSel} onChange={(e) => setSocietaId(e.target.value)} aria-label="Società">
            <option value="">Seleziona società</option>
            {listaSocieta.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-select" value={annoSel} onChange={(e) => cambiaAnno(Number(e.target.value))} aria-label="Anno">
          {ANNI.map((a) => <option key={a} value={a}>{modoSel === 'anni' ? `${a - 2}–${a}` : a}</option>)}
        </select>
        <select className="bop-select" value={meseDaSel} onChange={(e) => { const v = Number(e.target.value); setMeseDa(v); if (v > meseASel) setMeseA(v); }} aria-label="Da mese">
          {NOMI_MESI.map((m, i) => <option key={m} value={i + 1}>da {m.toLowerCase()}</option>)}
        </select>
        <select className="bop-select" value={meseASel} onChange={(e) => setMeseA(Number(e.target.value))} aria-label="A mese">
          {NOMI_MESI.map((m, i) => <option key={m} value={i + 1} disabled={i + 1 < meseDaSel}>a {m.toLowerCase()}</option>)}
        </select>
        <select className="bop-select" value={livello} onChange={(e) => setLivello(Number(e.target.value))} aria-label="Dettaglio">
          {LIVELLI.map((l) => <option key={l.valore} value={l.valore}>{l.etichetta}</option>)}
        </select>
        <BottoneCercaGo
          cercato={cercato}
          modificati={modificati}
          disabled={!societaSel || caricando}
          onClick={applica}
        />
      </div>

      <BopLoaderOverlay visibile={caricando} testo="Lettura della contabilità da Gestionale Open…" />

      {!societaId && (
        <div className="bop-empty-state">
          Seleziona una società. Il conto economico si legge una società alla volta: le voci
          sono quelle del piano dei conti di ciascuna.
        </div>
      )}
      {societaId && !cercato && <InvitoCercaGo />}
      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}
      {societaId && stato === 'NON_MAPPATA' && (
        <div className="bop-empty-state">
          Questa società non ha un database di Gestionale Open associato. Si imposta in
          {' '}<strong>Impostazioni Generali → Società del gruppo</strong>, campo «Database Gestionale Open».
        </div>
      )}
      {societaId && stato === 'NON_DISPONIBILE' && (
        <div className="bop-empty-state">
          <div className="bop-empty-state__title">Dati non ancora disponibili per questa società</div>
          <div className="bop-empty-state__text">{avviso}</div>
        </div>
      )}
      {caricando && !dati && <div className="bop-empty-state">Lettura della contabilità…</div>}

      {societaId && stato === 'OK' && dati && ultimaColonna && (
        <>
          <div className="bop-analisi-go__kpi">
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">Ricavi — {ultimaColonna.etichetta}</div>
              <div className="bop-analisi-go__stat-value">{euro(dati.ricavi[ultimaColonna.chiave])}</div>
              <div className="bop-analisi-go__stat-sub">{dati.periodo.descrizione}</div>
            </div>
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">Costi — {ultimaColonna.etichetta}</div>
              <div className="bop-analisi-go__stat-value">{euro(dati.costi[ultimaColonna.chiave])}</div>
              <div className="bop-analisi-go__stat-sub">in negativo, come nel conto economico</div>
            </div>
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">Risultato — {ultimaColonna.etichetta}</div>
              <div className="bop-analisi-go__stat-value"><Valore valore={dati.totali[ultimaColonna.chiave]} /></div>
              <div className="bop-analisi-go__stat-sub">
                {dati.ricavi[ultimaColonna.chiave]
                  ? `${formatNumber(Math.round((dati.totali[ultimaColonna.chiave] / dati.ricavi[ultimaColonna.chiave]) * 1000) / 10)}% dei ricavi`
                  : '—'}
              </div>
            </div>
          </div>

          {dati.periodo_aperto && (
            <div className="bop-alert bop-alert--warning">
              Periodo non ancora chiuso: gli assestamenti di fine esercizio arrivano con la chiusura
              del bilancio, quindi il risultato può ancora cambiare.
            </div>
          )}

          <div className="bop-card bop-conto-economico__card">
            <table className="bop-table bop-conto-economico">
              <thead>
                <tr>
                  <th>Voce</th>
                  {colonne.map((c) => <th key={c.chiave} className="bop-conto-economico__th-valore">{c.etichetta}</th>)}
                  {conTotale && <th className="bop-conto-economico__th-valore">Totale</th>}
                  {conVariazione && (
                    <>
                      <th className="bop-conto-economico__th-valore">Var. {penultima}→{ultima}</th>
                      <th className="bop-conto-economico__th-valore">Var. %</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {righeVisibili.map((n) => {
                  const haFigli = n.figli.length > 0;
                  const eConto = n.livello === 4;
                  return (
                    <tr
                      key={n.chiave}
                      className={`bop-conto-economico__riga bop-conto-economico__riga--l${n.livello}`}
                      onClick={() => (haFigli ? alterna(n.chiave) : eConto ? setContoAperto(n) : undefined)}
                      title={eConto ? 'Clic per le registrazioni del conto' : undefined}
                    >
                      <td className={`bop-conto-economico__voce bop-conto-economico__voce--l${n.livello}`}>
                        <span className="bop-conto-economico__freccia">{haFigli ? (aperti.has(n.chiave) ? '▾' : '▸') : ''}</span>
                        {n.descrizione ?? n.codice}
                        <span className="bop-conto-economico__codice">{n.codice}</span>
                      </td>
                      {cellaValori(n.valori)}
                    </tr>
                  );
                })}
                <tr className="bop-conto-economico__riga bop-conto-economico__riga--totale">
                  <td className="bop-conto-economico__voce">Totale ricavi</td>
                  {cellaValori(dati.ricavi)}
                </tr>
                <tr className="bop-conto-economico__riga bop-conto-economico__riga--totale">
                  <td className="bop-conto-economico__voce">Totale costi</td>
                  {cellaValori(dati.costi)}
                </tr>
                <tr className="bop-conto-economico__riga bop-conto-economico__riga--risultato">
                  <td className="bop-conto-economico__voce">Risultato</td>
                  {cellaValori(dati.totali)}
                </tr>
              </tbody>
            </table>
          </div>

          <p className="bop-analisi-go__nota">
            Database <strong>{dati.database}</strong> · ricavi positivi, costi negativi · clic su una voce per
            aprirla, su un conto per vederne le registrazioni.
          </p>
          {dati.criterio && <p className="bop-analisi-go__nota">{dati.criterio}</p>}
        </>
      )}

      {contoAperto && dati && (
        <ModaleRegistrazioni
          societaId={societaId}
          conto={contoAperto}
          colonne={dati.colonne}
          modo={dati.modo}
          onClose={() => setContoAperto(null)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Modale: registrazioni di un conto ──────────────────────────────────────────

function ModaleRegistrazioni({ societaId, conto, colonne, modo, onClose }: {
  societaId: string; conto: NodoCE; colonne: Colonna[]; modo: Modo; onClose: () => void;
}) {
  // Periodo: mese per mese si parte da tutto l'anno mostrato (''); negli anni a
  // confronto dall'anno piu' recente — "tutto" coprirebbe mesi fuori
  // dall'intervallo scelto.
  const [colonna, setColonna] = useState<string>(modo === 'mesi' ? '' : (colonne[colonne.length - 1]?.chiave ?? ''));
  const [risposta, setRisposta] = useState<RispostaRegistrazioni | null>(null);
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState('');

  const periodo = useMemo(() => (colonna
    ? colonne.find((c) => c.chiave === colonna) ?? colonne[colonne.length - 1]
    : { chiave: '', etichetta: 'Tutto il periodo', da: colonne[0]?.da ?? '', a: colonne[colonne.length - 1]?.a ?? '', criterio: 'competenze' as Criterio }),
  [colonna, colonne]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const carica = useCallback(async (offset: number) => {
    if (!periodo || !conto.codice) return;
    setCaricando(true);
    setErrore('');
    try {
      const p = new URLSearchParams({
        societaId, conto: conto.codice, da_data: periodo.da, a_data: periodo.a,
        criterio: periodo.criterio ?? 'competenze',
        limit: String(PAGINA_REGISTRAZIONI), offset: String(offset),
      });
      // @api GET /api/bop-go/conto-economico/registrazioni?societaId=1&conto=CO010614&da_data=2026-01-01&a_data=2026-08-31&criterio=competenze -> {"success":true,"stato":"OK","data":{"totale":"number","importo":"string","has_more":"boolean","data":[{"data_competenza":"string","causale":"string","descrizione":"string","dare":"string","avere":"string","importo":"string","tipo_competenza":"string","competenza_dal":"string","competenza_al":"string"}]}}
      const res = await apiFetch<BustaGo<RispostaRegistrazioni>>(`/api/bop-go/conto-economico/registrazioni?${p.toString()}`);
      if (res.stato !== 'OK' || !res.data) { setErrore(res.error || 'Dati GO non disponibili'); return; }
      const nuova = res.data;
      setRisposta((prec) => (offset > 0 && prec ? { ...nuova, data: [...prec.data, ...nuova.data] } : nuova));
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Errore nel recupero delle registrazioni');
    } finally {
      setCaricando(false);
    }
  }, [societaId, conto.codice, periodo]);

  useEffect(() => { setRisposta(null); void carica(0); }, [carica]);

  const colonneTabella = useMemo<BopExcelColumn<Registrazione>[]>(() => [
    { key: 'competenza', header: 'Competenza', accessor: (r) => r.data_competenza ?? '', render: (r) => dataIt(r.data_competenza), width: '110px' },
    { key: 'registrazione', header: 'Registrazione', accessor: (r) => r.data_registrazione ?? '', render: (r) => dataIt(r.data_registrazione), width: '120px', defaultHidden: true },
    { key: 'periodo', header: 'Periodo di competenza', accessor: (r) => (r.tipo_competenza === 'quota' ? `${r.competenza_dal ?? ''} ${r.competenza_al ?? ''}` : ''), render: (r) => (r.tipo_competenza === 'quota' ? `${dataIt(r.competenza_dal)} – ${dataIt(r.competenza_al)}` : ''), width: '190px' },
    { key: 'giorni', header: 'Giorni', accessor: (r) => r.giorni_nel_periodo ?? 0, render: (r) => (r.tipo_competenza === 'quota' ? `${r.giorni_nel_periodo} su ${r.giorni_totali}` : ''), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '110px' },
    { key: 'causale', header: 'Causale', accessor: (r) => r.descrizione_causale ?? r.causale ?? '', filter: { type: 'text' }, width: '190px' },
    { key: 'descrizione', header: 'Descrizione', accessor: (r) => r.descrizione ?? '', filter: { type: 'text' } },
    { key: 'documento', header: 'Documento', accessor: (r) => (r.numero_documento ? `${r.documento_tipo ?? ''} n. ${r.numero_documento}`.trim() : ''), width: '160px' },
    { key: 'controparte', header: 'Controparte', accessor: (r) => r.controparte_nome ?? '', filter: { type: 'text' } },
    { key: 'intero', header: 'Importo intero', accessor: (r) => num(r.importo_intero ?? r.importo), render: (r) => <Valore valore={num(r.importo_intero ?? r.importo)} />, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '130px' },
    { key: 'importo', header: 'Di competenza', accessor: (r) => num(r.importo), render: (r) => <strong className="bop-conto-economico__quota"><Valore valore={num(r.importo)} /></strong>, cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '130px' },
    { key: 'dare', header: 'Dare', accessor: (r) => num(r.dare), render: (r) => (num(r.dare) ? euro(r.dare) : ''), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '120px', defaultHidden: true },
    { key: 'avere', header: 'Avere', accessor: (r) => num(r.avere), render: (r) => (num(r.avere) ? euro(r.avere) : ''), cellClassName: 'bop-text-right', headerClassName: 'bop-text-right', width: '120px', defaultHidden: true },
  ], []);

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div className="bop-modal bop-modal--xl bop-dettaglio-go bop-dettaglio-go--largo" role="dialog" aria-modal="true" aria-label={conto.descrizione ?? ''} onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <div className="bop-dettaglio-go__intestazione">
            <h2 className="bop-modal__title">{conto.descrizione} ({conto.codice})</h2>
            <div className="bop-dettaglio-go__sottotitolo">
              {periodo.criterio === 'contabile'
                ? 'Registrazioni dell\'esercizio chiuso (bilancio contabile)'
                : 'Registrazioni per competenza: di un costo o ricavo con un periodo di competenza conta solo la quota dei giorni che cadono nel periodo scelto (importo intero × giorni nel periodo / giorni totali)'}
            </div>
          </div>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="bop-modal__body">
          <div className="bop-dettaglio-go__riepilogo">
            {colonne.length > 1 && (
              <select className="bop-select" value={colonna} onChange={(e) => setColonna(e.target.value)} aria-label="Periodo">
                {modo === 'mesi' && <option value="">Tutto il periodo ({dataIt(colonne[0]?.da)} – {dataIt(colonne[colonne.length - 1]?.a)})</option>}
                {colonne.map((c) => <option key={c.chiave} value={c.chiave}>{c.etichetta} ({dataIt(c.da)} – {dataIt(c.a)})</option>)}
              </select>
            )}
            {risposta && (
              <>
                <span className="bop-dettaglio-go__riepilogo-voce">{risposta.totale} registrazioni</span>
                <span className="bop-dettaglio-go__riepilogo-voce">Saldo <strong><Valore valore={num(risposta.importo)} /></strong></span>
              </>
            )}
          </div>
          {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}
          {caricando && !risposta && <div className="bop-empty-state">Lettura delle registrazioni…</div>}
          {risposta && (
            <>
              <BopExcelTable<Registrazione>
                data={risposta.data}
                columns={colonneTabella}
                rowKey={(r) => (r.tipo_competenza === 'quota' ? `q-${r.progressivo}-${r.riga}-${r.competenza_dal}-${r.data_competenza}` : String(r.id_riga))}
                loading={caricando}
                initialSort={{ key: 'competenza', dir: 'asc' }}
                storageKey="conto-economico-go-registrazioni-v2"
                resizableColumns
                emptyState={<div className="bop-empty-state">Nessuna registrazione nel periodo</div>}
              />
              {risposta.has_more && (
                <button className="bop-btn bop-btn-secondary" disabled={caricando} onClick={() => void carica(risposta.data.length)}>
                  Carica altre {PAGINA_REGISTRAZIONI}
                </button>
              )}
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
